/* eslint-disable jsx-a11y/media-has-caption */
import { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import to from 'await-to-js'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import axios from 'axios'
import { Container, Section, Button } from 'chaoskit/src/components'
import { text, link, gradient } from 'chaoskit/src/assets/styles/utility'
import Avatar, { StylesAvatarVariables } from 'chaoskit/src/components/Avatar'
import useMount from 'react-use/lib/useMount'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'

import BaseLayout from '~layouts/BaseLayout'
import Data from '~components/Data'
import Hero, {
  HeroContent,
  HeroTitleWrapper,
  HeroTitleSub,
  HeroTitle,
} from '~partials/Hero'
import StaticFeaturedArticles from '~partials/global/StaticFeaturedArticles'
import HelmetSEO from '~partials/global/HelmetSEO'
import { shadowEmboss, stickyIt } from '~styles/utility'
import { logos as customerLogos } from '~partials/global/CustomerLogos'
import { VideoModal } from '~components/Video'
import LogosGrid from '~components/LogosGrid'
import track from '~helpers/track'
import { CaseStudyTeasers } from '~components/case-studies/UI'

const TopLogos = ({ teams }) => {
  const theme = useTheme()

  return (
    <Section>
      <Container>
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,

            [theme.mq.medium]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: theme.space.xlarge,
            },
          }}
        >
          <div>
            <h3 css={stickyIt(theme)}>
              <Data>{teams.heading}</Data>
            </h3>
          </div>
          <LogosGrid logos={customerLogos} />
        </div>
      </Container>
    </Section>
  )
}

TopLogos.propTypes = {
  teams: PropTypes.object.isRequired,
}

// Converts plain-text into clickable links
// @NOTE Because the Twitter API says, "oh, no one wants links"
const tweetRegex = (tweetText) => {
  const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g
  const hashRegex = /\s([#][\w_-]+)/g
  const mentionRegex = /\s([@][\w_-]+)/g

  const urlReplace = tweetText.replace(
    urlRegex,
    (match) =>
      `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`
  )

  const hashReplace = urlReplace.replace(
    hashRegex,
    (match, match1) =>
      ` <a href="https://www.twitter.com/hashtag/${match1.replace(
        '#',
        ''
      )}" target="_blank" rel="noopener noreferrer">${match1}</a>`
  )

  const mentionReplace = hashReplace.replace(
    mentionRegex,
    (match, match1) =>
      ` <a href="https://www.twitter.com/${match1.replace(
        '@',
        ''
      )}" target="_blank" rel="noopener noreferrer">${match1}</a>`
  )

  return mentionReplace
}

const CustomerTweet = ({ tweet, isEven }) => {
  const theme = useTheme()

  return (
    <article
      className="Customers__CustomerTweet"
      css={[
        shadowEmboss(theme, {
          direction: 'left',
        }),
        {
          padding: theme.space.large,
          background: theme.color.light.base,
          borderRadius: theme.borderRadius.base,
          transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
          transformOrigin: 'center center',
        },

        isEven && {
          zIndex: 2,

          [theme.mq.medium]: {
            transform: `translateX(${theme.space.large}px)`,
            marginTop: -theme.space.base,
            marginBottom: -theme.space.base,
          },
        },
      ]}
    >
      <div
        css={{
          display: 'grid',
          alignItems: 'center',
          gridTemplateColumns: `${
            StylesAvatarVariables(theme).size.small
          }px 1fr`,
          gap: theme.space.small,
        }}
      >
        <Avatar size="small" image={tweet.user.profile_image_url_https} />

        <div>
          <div
            css={[
              text.heading(theme),
              {
                fontWeight: theme.fontWeight.bold,
                fontSize: theme.fontSize.medium,
              },
            ]}
          >
            {tweet.user.name}
          </div>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`https://www.twitter.com/${tweet.user.screen_name}`}
            css={[
              link.muted(theme),
              link.linkUnderline,
              {
                fontSize: theme.fontSize.xsmall,
              },
            ]}
          >
            @{tweet.user.screen_name}
          </a>
        </div>
      </div>
      <div
        css={{
          fontSize: theme.fontSize.small,
          marginTop: theme.space.medium,

          a: [
            link.linkUnderline,
            {
              color: theme.brand.teal,
              fontWeight: theme.fontWeight.bold,
            },
          ],
        }}
      >
        <Data>{tweetRegex(tweet.full_text)}</Data>
      </div>
    </article>
  )
}

CustomerTweet.propTypes = {
  tweet: PropTypes.object.isRequired,
  isEven: PropTypes.bool,
}

const Customers = ({
  data: {
    pageData: {
      childMdx: { frontmatter: pageData },
    },
    allCaseStudies: { edges: allCaseStudies },
  },
}) => {
  const theme = useTheme()

  const videoRef = useRef()

  const [customerVideoModalOpen, setCustomerVideoModalOpen] = useState(false)
  const [tweets, setTweets] = useState([])

  // Grab IDs from statuses
  const getStatuses = async () => {
    const statusList = pageData.community.tweets
      .map(({ status }) => {
        const regex = /http(s)?:\/\/twitter\.com\/\w+\/status(es)?\/(\d+)/

        const match = status.match(regex)

        if (match) {
          return match[3]
        }

        return false
      })
      .join(',')

    const [, data] = await to(
      axios.post('/.netlify/functions/tweets', {
        statuses: statusList,
      })
    )

    if (data) {
      setTweets(data.data?.statuses)
    }
  }

  useMount(() => {
    getStatuses()
  })

  useUpdateEffect(() => {
    if (customerVideoModalOpen) {
      videoRef.current.pause()
    } else {
      videoRef.current.play()
    }
  }, [customerVideoModalOpen])

  return (
    <BaseLayout>
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <Hero
        css={{
          ...gradient.dark(theme),

          '&::before': {
            content: "''",
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: theme.opacity.base,
            zIndex: 1,
            background: theme.color.dark.base,
          },
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          css={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src={pageData.hero.backgroundVideo} />
        </video>
        <HeroContent>
          <Container>
            <HeroTitleWrapper>
              <HeroTitle size="large">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub>
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
              <div css={{ marginTop: theme.space.large }}>
                <Button
                  type="primary"
                  noContrast
                  onClick={() => {
                    track('Video watched', {
                      label: 'Featured customer',
                    })

                    setCustomerVideoModalOpen(true)
                  }}
                >
                  Watch now
                </Button>
                <VideoModal
                  open={customerVideoModalOpen}
                  setIsOpen={setCustomerVideoModalOpen}
                  title="Customer video"
                  src={pageData.hero.video}
                />
              </div>
            </HeroTitleWrapper>
          </Container>
        </HeroContent>
      </Hero>
      <TopLogos teams={pageData.teams} />
      <Section
        space="xlarge"
        css={{
          paddingTop: '0 !important',

          [theme.mq.medium]: {
            paddingBottom: `${theme.space.large}px !important`,
          },
        }}
      >
        <Container>
          <CaseStudyTeasers caseStudies={allCaseStudies} />
        </Container>
      </Section>
      {tweets.length > 0 && (
        <Section
          css={{
            position: 'relative',

            [theme.mq.small__max]: {
              background: theme.color.dark.base,
            },
          }}
        >
          <Container>
            <div
              css={{
                display: 'grid',
                gap: theme.space.large,

                [theme.mq.medium]: {
                  gridTemplateColumns: '1fr 2fr',
                  gap: theme.space.xlarge,
                  position: 'relative',

                  '&::before': {
                    content: "''",
                    width: 'var(--rw)',
                    left: '50%',
                    position: 'absolute',
                    background: theme.color.dark.base,
                    transform: 'translate(-50%, -50%)',
                    zIndex: -1,
                    top: '50%',
                    height: `calc(100% - ${theme.space.xlarge}px)`,
                  },
                },
              }}
            >
              <div
                css={{
                  [theme.mq.medium]: {
                    paddingTop: theme.space.xlarge,
                    paddingBottom: theme.space.xlarge,
                  },
                }}
              >
                <h3 className="u-contrast" css={stickyIt(theme)}>
                  <Data>{pageData.community.heading}</Data>
                </h3>
              </div>

              <div
                css={{
                  display: 'grid',
                  gap: theme.space.base,

                  [theme.mq.medium]: {
                    gap: 0,

                    '&:hover, &:focus': {
                      '.Customers__CustomerTweet': {
                        transform: 'scale(1.025)',
                      },
                    },
                  },
                }}
              >
                {tweets.map((tweet, index) => (
                  <CustomerTweet
                    key={tweet.id_str}
                    tweet={tweet}
                    isEven={(index + 1) % 2 === 0}
                  />
                ))}
              </div>
            </div>
          </Container>
        </Section>
      )}
      <StaticFeaturedArticles
        css={{
          marginTop: tweets.length === 0 && theme.space.xlarge,
          background: `linear-gradient(to bottom, ${theme.color.light.base}, ${theme.color.panel.base})`,

          [theme.mq.medium]: {
            paddingTop: '0 !important',
          },
        }}
      />
    </BaseLayout>
  )
}

Customers.propTypes = {
  data: PropTypes.object.isRequired,
}

export const pageQuery = graphql`
  query CustomersPageData {
    pageData: file(
      name: { eq: "customers" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            blurb
            heading
            video
            backgroundVideo
          }
          teams {
            heading
          }
          community {
            heading
            tweets {
              status
            }
          }
        }
      }
    }

    allCaseStudies: allMdx(
      sort: { fields: [frontmatter___priority] }
      filter: {
        # Make sure we're only querying case studies
        fileAbsolutePath: { regex: "/src/case-studies/" }
        frontmatter: { title: { ne: "BLUEPRINT" } }
      }
    ) {
      edges {
        node {
          ...CaseStudyTeaserFragment
        }
      }
    }
  }
`

export default Customers
